import { Injectable } from '@angular/core';
import { PhoneNumberUtil } from 'google-libphonenumber';
import type { Language } from './language/language.service';

export interface CountryCode {
  region: string;
  code: string;
  flag: string;
  name: string;
}

export interface LocalizedCountry {
  code: string;
  flag: string;
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class UtilityService {
  private phoneUtil = PhoneNumberUtil.getInstance();
  private readonly supportedLanguages: readonly Language[] = ['es', 'en', 'pt', 'fr', 'ja'];
  private readonly languageLocales: Record<Language, string> = {
    es: 'es',
    en: 'en',
    pt: 'pt-BR',
    fr: 'fr',
    ja: 'ja-JP'
  };
  private readonly localizedCountriesCache = new Map<Language, LocalizedCountry[]>();
  private readonly countryLookupCache = new Map<Language, Map<string, string>>();

  // Lista básica de banderas y nombres (puedes expandirla)
  private regionData: { [key: string]: { flag: string; name: string } } = {
    'CO': { flag: '🇨🇴', name: 'Colombia' },
    'US': { flag: '🇺🇸', name: 'Estados Unidos' },
    'ES': { flag: '🇪🇸', name: 'España' },
    'MX': { flag: '🇲🇽', name: 'México' },
    'BR': { flag: '🇧🇷', name: 'Brasil' },
    'GB': { flag: '🇬🇧', name: 'Reino Unido' },
    'DE': { flag: '🇩🇪', name: 'Alemania' },
    'FR': { flag: '🇫🇷', name: 'Francia' },
    'JP': { flag: '🇯🇵', name: 'Japón' },
    // ...agrega más según necesidad
  };

  getCountryCodes(): CountryCode[] {
    const supportedRegions = this.phoneUtil.getSupportedRegions();
    const codeMap: { [code: string]: { regions: string[], flags: string[], names: string[] } } = {};
    Array.from(supportedRegions).forEach(region => {
      const code = '+' + this.phoneUtil.getCountryCodeForRegion(region);
      const flag = this.regionData[region]?.flag || '';
      const name = this.regionData[region]?.name || region;
      if (!codeMap[code]) {
        codeMap[code] = { regions: [], flags: [], names: [] };
      }
      codeMap[code].regions.push(region);
      if (flag) codeMap[code].flags.push(flag);
      codeMap[code].names.push(name);
    });
    return Object.entries(codeMap).map(([code, data]) => {
      return {
        region: data.regions.join(','),
        code,
        flag: data.flags.join(' '),
        name: data.names.join(', ')
      };
    }).sort((a, b) => a.code.localeCompare(b.code));
  }

  getLocalizedCountries(language: Language): LocalizedCountry[] {
    const cachedCountries = this.localizedCountriesCache.get(language);
    if (cachedCountries) {
      return cachedCountries;
    }

    const displayNames = this.createRegionDisplayNames(language);
    const countries = Array.from(this.phoneUtil.getSupportedRegions())
      .filter(region => /^[A-Z]{2}$/.test(region))
      .map(region => ({
        code: region,
        flag: this.toFlagEmoji(region),
        name: displayNames?.of(region) || this.regionData[region]?.name || region
      }))
      .sort((left, right) =>
        left.name.localeCompare(right.name, this.languageLocales[language], { sensitivity: 'base' })
      );

    this.localizedCountriesCache.set(language, countries);
    return countries;
  }

  getLocalizedCountryName(countryValue: string, language: Language): string | null {
    const countryCode = this.findCountryCode(countryValue);
    if (!countryCode) {
      return null;
    }

    return this.getLocalizedCountries(language).find(country => country.code === countryCode)?.name || null;
  }

  findCountryCode(countryValue: string): string | null {
    const normalizedValue = this.normalizeCountryValue(countryValue);
    if (!normalizedValue) {
      return null;
    }

    const trimmedValue = countryValue.trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(trimmedValue) && this.getLocalizedCountries('en').some(country => country.code === trimmedValue)) {
      return trimmedValue;
    }

    for (const language of this.supportedLanguages) {
      const countryCode = this.getCountryLookup(language).get(normalizedValue);
      if (countryCode) {
        return countryCode;
      }
    }

    return null;
  }

  validateInternationalNumberDetailed(number: string, region: string): { valid: boolean; reason?: string } {
    try {
      if ((region === "AG,AI,AS,BB,BM,BS,CA,DM,DO,GD,GU,JM,KN,KY,LC,MP,MS,PR,SX,TC,TT,US,VC,VG,VI")) {
        region = "US";
      }
      // Permitir números de prueba en desarrollo
      const isDev = !('production' === (window as any)?.environment?.mode || (window as any)?.__env?.production);
      if (isDev && region === "US" && /^\+?1?555/.test(number.replace(/\D/g, ''))) {
        return { valid: true };
      }
      const parsed = this.phoneUtil.parse(number, region);
      // Comprueba si es un número posible (longitud/estructura básica)
      if (!this.phoneUtil.isPossibleNumber(parsed)) {
        return { valid: false, reason: 'not_possible' };
      }
      // Comprueba si es un número válido asignado en el plan de numeración
      if (!this.phoneUtil.isValidNumber(parsed)) {
        return { valid: false, reason: 'not_valid' };
      }
      return { valid: true };
    } catch (e: any) {
      // Errores de parseado suelen indicar formato inválido
      return { valid: false, reason: 'invalid_format' };
    }
  }

  private createRegionDisplayNames(language: Language): Intl.DisplayNames | null {
    if (typeof Intl.DisplayNames !== 'function') {
      return null;
    }

    return new Intl.DisplayNames([this.languageLocales[language]], { type: 'region' });
  }

  private getCountryLookup(language: Language): Map<string, string> {
    const cachedLookup = this.countryLookupCache.get(language);
    if (cachedLookup) {
      return cachedLookup;
    }

    const lookup = new Map<string, string>();
    for (const country of this.getLocalizedCountries(language)) {
      lookup.set(this.normalizeCountryValue(country.name), country.code);
      lookup.set(this.normalizeCountryValue(country.code), country.code);
    }

    this.countryLookupCache.set(language, lookup);
    return lookup;
  }

  private normalizeCountryValue(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  private toFlagEmoji(regionCode: string): string {
    if (!/^[A-Z]{2}$/.test(regionCode)) {
      return '';
    }

    return String.fromCodePoint(...regionCode.split('').map(char => 127397 + (char.codePointAt(0) || 0)));
  }
}
